/**
 * CancelMembershipModal — self-serve membership cancellation.
 *
 * Shows two paths per the locked policy with the owner:
 *   1. "Talk to coach" — opens the in-app messages thread (less friction
 *      for save attempts).
 *   2. "Cancel anyway" — calls POST /api/me/cancel-membership; warns
 *      explicitly that unused sessions will be FORFEITED.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { X, AlertTriangle, MessageCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CancelMembershipModal({ open, onClose, renewItem, onCancelled }) {
    const navigate = useNavigate();
    const [step, setStep] = useState("intro"); // intro | confirm | submitting | done
    const [reason, setReason] = useState("");
    const [result, setResult] = useState(null);
    // 2:1 conversion preview — fetched when modal opens so member sees
    // EXACTLY how many sessions they keep vs. forfeit before they confirm.
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        if (!open) return;
        axios
            .get(`${API}/me/cancel-preview`, { withCredentials: true })
            .then((r) => setPreview(r.data))
            .catch(() => setPreview(null));
    }, [open]);

    if (!open) return null;

    const reset = () => {
        setStep("intro");
        setReason("");
        setResult(null);
    };

    const handleClose = () => {
        reset();
        onClose?.();
    };

    const handleCancelAnyway = async () => {
        setStep("submitting");
        try {
            const r = await axios.post(
                `${API}/me/cancel-membership`,
                { confirm: true, reason },
                { withCredentials: true }
            );
            setResult(r.data);
            setStep("done");
            toast.success("Membership cancelled.");
            onCancelled?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Cancellation failed. Try again or text the gym.");
            setStep("confirm");
        }
    };

    const itemLabel = renewItem?.plan_name || renewItem?.display_name || "your membership";

    return (
        <div
            data-testid="cancel-modal-backdrop"
            className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={handleClose}
        >
            <div
                data-testid="cancel-modal"
                onClick={(e) => e.stopPropagation()}
                className="bg-zinc-950 border border-zinc-800 rounded-2xl max-w-md w-full overflow-hidden shadow-2xl"
            >
                <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red">Cancel membership</div>
                    <button
                        type="button"
                        data-testid="cancel-modal-close"
                        onClick={handleClose}
                        className="text-zinc-400 hover:text-white"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {step === "intro" && (
                    <div className="p-5 space-y-4">
                        <h2 className="font-display text-2xl tracking-wider text-white">
                            Hold up — let's pick the right path.
                        </h2>
                        <p className="text-sm text-zinc-300 leading-relaxed">
                            Cancelling <span className="text-white font-semibold">{itemLabel}</span> is a one-tap deal, but it
                            comes with consequences. Most folks find that a quick chat with a coach gets them what they actually
                            need (schedule change, pause, plan swap) — not full cancellation.
                        </p>
                        <button
                            type="button"
                            data-testid="cancel-talk-to-coach"
                            onClick={() => {
                                handleClose();
                                navigate("/dashboard?tab=messages");
                            }}
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-brand-red hover:bg-brand-red/90 text-white font-medium transition-colors"
                        >
                            <MessageCircle className="w-4 h-4" />
                            Talk to a coach first
                        </button>
                        <button
                            type="button"
                            data-testid="cancel-proceed"
                            onClick={() => setStep("confirm")}
                            className="w-full px-4 py-3 rounded-xl border border-zinc-700 text-zinc-200 hover:border-zinc-500 hover:bg-zinc-900 text-sm transition-colors"
                        >
                            Cancel anyway
                        </button>
                    </div>
                )}

                {step === "confirm" && (
                    <div className="p-5 space-y-4">
                        <div className="flex items-start gap-3 p-3 rounded-xl border border-amber-600/40 bg-amber-500/10">
                            <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
                            <div className="text-sm text-amber-100">
                                <p className="font-semibold mb-1">Heads up — this is final.</p>
                                <ul className="list-disc pl-4 space-y-1 text-amber-50 text-[13px]">
                                    <li>Your Stripe subscription cancels <em>immediately</em>.</li>
                                    {preview && preview.sessions_active > 0 ? (
                                        <li>
                                            Unused sessions convert at <strong>2:1</strong> — you'll{" "}
                                            <strong>keep {preview.sessions_kept_on_cancel}</strong> of your current{" "}
                                            <strong>{preview.sessions_active}</strong> session
                                            {preview.sessions_active !== 1 ? "s" : ""}, and{" "}
                                            <strong>{preview.sessions_forfeited_on_cancel}</strong> will be forfeited.
                                        </li>
                                    ) : (
                                        <li>
                                            Any future unused sessions convert at <strong>2:1</strong> on cancel
                                            (e.g. 4 left → 2 kept).
                                        </li>
                                    )}
                                    <li>
                                        <span data-testid="cancel-why-2to1">
                                            Why half? Without an active membership, sessions are billed at the{" "}
                                            <strong>non-member single-session rate (~$85)</strong>, which is roughly{" "}
                                            <strong>2× the member rate</strong>. The 2:1 conversion preserves the dollar value you already paid.
                                        </span>
                                    </li>
                                    <li>You'll lose member pricing if you come back later.</li>
                                </ul>
                            </div>
                        </div>
                        {preview && preview.sessions_active > 0 && (
                            <div
                                data-testid="cancel-preview-summary"
                                className="grid grid-cols-3 gap-2 rounded-xl border border-zinc-800 bg-zinc-900/40 p-3 text-center"
                            >
                                <div>
                                    <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-400">Today</div>
                                    <div className="text-2xl font-mono text-white font-bold">{preview.sessions_active}</div>
                                </div>
                                <div>
                                    <div className="text-[9px] tracking-[0.25em] uppercase text-emerald-400">After 2:1</div>
                                    <div className="text-2xl font-mono text-emerald-300 font-bold">{preview.sessions_kept_on_cancel}</div>
                                </div>
                                <div>
                                    <div className="text-[9px] tracking-[0.25em] uppercase text-zinc-400">Forfeited</div>
                                    <div className="text-2xl font-mono text-zinc-500 font-bold">{preview.sessions_forfeited_on_cancel}</div>
                                </div>
                            </div>
                        )}
                        <div>
                            <label className="block text-[11px] tracking-[0.25em] uppercase text-zinc-400 mb-1.5">
                                Reason (optional)
                            </label>
                            <textarea
                                data-testid="cancel-reason"
                                value={reason}
                                onChange={(e) => setReason(e.target.value.slice(0, 300))}
                                rows={3}
                                placeholder="Helps us improve. Honest feedback welcome."
                                className="w-full bg-black border border-zinc-700 rounded-lg p-2 text-sm text-white focus:border-brand-red focus:outline-none"
                            />
                            <div className="text-[10px] text-zinc-500 text-right mt-1">{reason.length}/300</div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <button
                                type="button"
                                data-testid="cancel-go-back"
                                onClick={() => setStep("intro")}
                                className="px-4 py-2.5 rounded-xl border border-zinc-700 text-zinc-200 hover:border-zinc-500 hover:bg-zinc-900 text-sm transition-colors"
                            >
                                Go back
                            </button>
                            <button
                                type="button"
                                data-testid="cancel-confirm-final"
                                onClick={handleCancelAnyway}
                                className="px-4 py-2.5 rounded-xl bg-brand-red hover:bg-brand-red/90 text-white text-sm font-medium transition-colors"
                            >
                                Cancel now
                            </button>
                        </div>
                    </div>
                )}

                {step === "submitting" && (
                    <div data-testid="cancel-submitting" className="p-8 flex flex-col items-center justify-center gap-3 text-zinc-300">
                        <Loader2 className="w-6 h-6 animate-spin text-brand-red" />
                        <span className="text-sm">Cancelling…</span>
                    </div>
                )}

                {step === "done" && result && (
                    <div data-testid="cancel-done" className="p-5 space-y-4">
                        <h2 className="font-display text-2xl tracking-wider text-white">Cancelled.</h2>
                        <ul className="text-sm text-zinc-300 space-y-1 leading-relaxed">
                            <li>Subscriptions cancelled: <span className="text-white">{result.subscriptions_cancelled}</span></li>
                            <li>Commitments closed: <span className="text-white">{result.commitments_cancelled}</span></li>
                            <li>Sessions kept (2:1): <span className="text-emerald-300">{result.sessions_kept || 0}</span></li>
                            <li>Sessions forfeited: <span className="text-white">{result.sessions_forfeited}</span></li>
                        </ul>
                        <p className="text-[12px] text-zinc-500">
                            A confirmation email is on its way. Changed your mind? Reply to that email or text the gym.
                        </p>
                        <button
                            type="button"
                            data-testid="cancel-done-close"
                            onClick={handleClose}
                            className="w-full px-4 py-2.5 rounded-xl border border-zinc-700 text-zinc-200 hover:border-zinc-500 hover:bg-zinc-900 text-sm transition-colors"
                        >
                            Close
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
